import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { configApi } from '../api/config';
import { LandingShell, LandingTop } from '../components/landing/LandingShell';
import { openConsentSettings } from '../lib/consent';

/**
 * Every cookie and every stored value, by name, with what it is for and how long it lives.
 *
 * The rows are the truth about the code, not a template: the first group is what the site sets
 * for everybody because it cannot work without it, and needs no consent; the second and third
 * exist only when a tag is configured *and* the visitor has agreed — so when nothing is
 * configured they are not listed, because they are not set.
 */
const NECESSARY = ['auth', 'xsrf', 'consent', 'language', 'theme', 'progress'] as const;
const ANALYTICS = ['ga', 'gaContainer', 'acquisition'] as const;
const MARKETING = ['gclAu', 'gclAw'] as const;

export default function CookiesPage() {
  const { t } = useTranslation();
  const { data: config } = useQuery({ queryKey: ['config'], queryFn: configApi.get });

  const tracking = config?.tracking ?? null;

  const table = (rows: readonly string[]) => (
    <div className="l-table-scroll">
      <table className="l-cookie-table">
        <thead>
          <tr>
            <th>{t('cookies.columns.name')}</th>
            <th>{t('cookies.columns.purpose')}</th>
            <th>{t('cookies.columns.duration')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row}>
              <td>
                <code>{t(`cookies.rows.${row}.name`)}</code>
              </td>
              <td>{t(`cookies.rows.${row}.purpose`)}</td>
              <td>{t(`cookies.rows.${row}.duration`)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <LandingShell>
      <LandingTop>
        <section className="l-hero l-hero--compact">
          <div>
            <p className="l-eyebrow l-eyebrow--onDark">{t('marketing.legalEyebrow')}</p>
            <h1>{t('cookies.title')}</h1>
            <p className="l-hero-sub">{t('cookies.lead')}</p>
          </div>
        </section>
      </LandingTop>

      <section className="l-section">
        <div className="l-prose">
          <h2>{t('cookies.necessary.title')}</h2>
          <p>{t('cookies.necessary.body')}</p>
          {table(NECESSARY)}

          {tracking?.googleAnalyticsId && (
            <>
              <h2>{t('cookies.analytics.title')}</h2>
              <p>{t('cookies.analytics.body')}</p>
              {table(ANALYTICS)}
            </>
          )}

          {tracking?.googleAdsId && (
            <>
              <h2>{t('cookies.marketing.title')}</h2>
              <p>{t('cookies.marketing.body')}</p>
              {table(MARKETING)}
            </>
          )}

          <h2>{t('cookies.account.title')}</h2>
          <p>{t('cookies.account.body')}</p>

          <h2>{t('cookies.change.title')}</h2>
          <p>{t('cookies.change.body')}</p>
          {tracking && (
            <p>
              <button type="button" className="l-btn" onClick={openConsentSettings}>
                {t('consent.settings')}
              </button>
            </p>
          )}
        </div>
      </section>
    </LandingShell>
  );
}
